import React, { useMemo, useState } from "react";

import {
  Box,
  Button,
  Grid,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";

import AlertDialog from "./AlertDialog";
import StannupMailQR from "../../assets/dashboard/stannup_QR.png";
import StannupMailContent from "../../assets/dashboard/stannup_content.png";
import VerificationModal from "../../assets/dashboard/verification_modal.png";
import AddressVerificationScreen from "../../assets/dashboard/address_verification_list.png";
import "../../styles/dashboard.scss";

/** Webpack emits root-relative `/static/media/...` paths; pin them to the page
 * origin so a quirky WebView base URI cannot resolve them under `/create-account/`
 * or another route segment. */
function resolveAssetUrl(assetPath) {
  if (!assetPath || typeof assetPath !== "string") {
    return "";
  }
  if (/^(https?:|data:|blob:)/i.test(assetPath)) {
    return assetPath;
  }
  if (typeof window === "undefined" || !window.location?.origin) {
    return assetPath;
  }
  return `${window.location.origin}${
    assetPath.startsWith("/") ? "" : "/"
  }${assetPath}`;
}

function ExampleImage({ src, alt, className, width, height }) {
  const [failed, setFailed] = useState(false);
  const resolvedSrc = useMemo(() => resolveAssetUrl(src), [src]);

  if (!resolvedSrc || failed) {
    return (
      <Box className={`stannup-mail-img stannup-mail-fallback ${className || ""}`}>
        <Typography variant="body2" color="text.secondary">
          Example image unavailable. Continue to Address Verification to enter
          your postcard code.
        </Typography>
      </Box>
    );
  }

  return (
    <img
      src={resolvedSrc}
      className={className}
      alt={alt}
      width={width}
      height={height}
      loading="eager"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

const buildSteps = () => [
  {
    id: 1,
    content: (
      <>
        <Stack direction="column" className="w-100">
          <Typography className="mb-3">
            To verify the authenticity of the property in the property list,{" "}
            <b>
              you can scan the QR code and enter the provided token on the
              verification modal.
            </b>{" "}
            This will confirm that the property is genuine and legally
            registered, giving you peace of mind and ensuring a smooth
            transaction process.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={4}>
              <ExampleImage
                src={StannupMailQR}
                className="stannup-mail-qr-img stannup-mail-img"
                alt="Postcard QR code example"
              />
            </Grid>
            <Grid item xs={12} sm={8} md={8}>
              <ExampleImage
                src={StannupMailContent}
                className="stannup-mail-content-img stannup-mail-img"
                alt="Postcard contents example"
              />
            </Grid>
          </Grid>
        </Stack>
      </>
    ),
  },
  {
    id: 2,
    content: (
      <>
        <Stack direction="column" className="w-100">
          <Typography className="mb-3">
            When you click on the Verify button,{" "}
            <b>
              a modal window will open where you will need to fill in a token
              sent to your mailing address.
            </b>{" "}
            Once you receive the postcard, open it and find the token. It should
            be a series of seven numbers that are unique to your verification
            request.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ExampleImage
                src={AddressVerificationScreen}
                className="stannup-mail-qr-img stannup-mail-img"
                alt="Address verification list example"
              />
            </Grid>
          </Grid>
        </Stack>
      </>
    ),
  },
  {
    id: 3,
    content: (
      <>
        <Stack direction="column" className="w-100">
          <Typography className="mb-3">
            Please, Look at the image below to find out how to enter the
            security code.
          </Typography>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              className="d-flex justify-content-center align-items-center"
            >
              <ExampleImage
                src={VerificationModal}
                className="stannup-mail-qr-img3 stannup-mail-img"
                alt="Security code entry example"
              />
            </Grid>
          </Grid>
        </Stack>
      </>
    ),
  },
];

const StannupInfoModal = ({
  isTailwindComponent = false,
  openStanupPopup,
  setOpenStanupPopup = () => {},
  scrollToAddressVerification = () => {},
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [showMobileStepper, setShowMobileStepper] = useState(0);
  const steps = useMemo(() => buildSteps(), []);

  // stepper to show how to verify address
  const stepper = () => {
    return (
      <>
        <Box className="stannup-example-wrapper">
          {/* Elegant Top Banner */}
          <Box className="stannup-example-banner">
            <Stack className="stannup-example-stack">
              <Box className="stannup-example-icon-wrapper">
                <span className="stannup-example-icon">✨</span>
              </Box>
              <Typography variant="h6" className="stannup-example-title">
                Example
              </Typography>
              <Typography variant="body2" className="stannup-example-subtitle">
                <span className="stannup-example-subtitle-full">
                  For demonstration purposes only
                </span>
                <span className="stannup-example-subtitle-short">
                  For demonstration purposes only
                </span>
              </Typography>
            </Stack>
          </Box>

          <Typography
            variant="h5"
            textAlign="left"
            mb={2}
            className="stannup-example-main-title"
          >
            <b>Property Verification Steps</b>
          </Typography>

          <Stepper activeStep={activeStep} nonLinear>
            {steps?.map((step) => (
              <Step key={step?.id}>
                <StepLabel></StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box className="my-4 stannup-example-content-box">
            {steps[activeStep]?.content}
          </Box>
          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            padding={{ xs: 3 }}
            justifyContent="space-between"
          >
            {activeStep > 0 ? (
              <Button
                variant="contained"
                className="popup_Btn other_popup_btn mb-2"
                disabled={activeStep === 0}
                onClick={() => {
                  setActiveStep((prevStep) => prevStep - 1);
                }}
              >
                Back
              </Button>
            ) : (
              <p>&nbsp;</p>
            )}

            {activeStep + 1 < steps?.length && (
              <Button
                variant="contained"
                className="popup_Btn success_popup_btn mb-2"
                onClick={() => {
                  setActiveStep((prevStep) => prevStep + 1);
                }}
              >
                Next
              </Button>
            )}
            {activeStep + 1 === steps?.length && (
              <Button
                className="popup_Btn success_popup_btn mb-2"
                onClick={() => {
                  setOpenStanupPopup(false);
                  localStorage.removeItem("stanupPopupCount");
                  scrollToAddressVerification();
                }}
              >
                Go To Address Verification
              </Button>
            )}
          </Stack>
        </Box>
      </>
    );
  };
  return (
    <>
      {openStanupPopup && (
        <AlertDialog
          isTailwindComponent={isTailwindComponent}
          type={showMobileStepper ? "" : "Attention"}
          openPopup={openStanupPopup}
          onClose={activeStep + 1 === steps?.length}
          width={showMobileStepper ? "md" : "sm"}
          onCloseFun={setOpenStanupPopup}
          mesage={
            <>
              {showMobileStepper ? (
                stepper()
              ) : (
                <p className="fw-400 mt-3">
                  Your account has some properties which need to be verified by
                  you. We’ve sent a postcard to your mailing address after
                  successful creation of your account. Once you receive the
                  postcard please go to the <b>Address verification</b> section
                  and enter the code found on the postcard to verify the
                  respective property address.
                </p>
              )}
            </>
          }
          buttons={
            !showMobileStepper && (
              <Button
                className="popup_Btn success_popup_btn"
                onClick={() => {
                  setShowMobileStepper(true);
                }}
              >
                Ok, All Good
              </Button>
            )
          }
          hideButtondiv={!showMobileStepper}
        />
      )}
    </>
  );
};

export default StannupInfoModal;
